import { MAPBOX_TOKEN } from './constants';

// ─── Mapbox Geocoding API (primary — more precise for BR house numbers) ───────
async function geocodeMapbox({ logradouro, numero, bairro, cidade, uf, cep }) {
  try {
    const partes = [
      numero ? `${logradouro} ${numero}` : logradouro,
      bairro,
      cidade,
      uf,
      'Brasil',
    ].filter(Boolean);

    const query = encodeURIComponent(partes.join(', '));
    const url   =
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json` +
      `?country=BR&language=pt&limit=1&access_token=${MAPBOX_TOKEN}`;
    // Adiciona proximity ao centroide do Brasil para priorizar resultados BR
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const feat = data?.features?.[0];
    if (feat) {
      const [lng, lat] = feat.center;
      return { lat, lng };
    }
    return null;
  } catch {
    return null;
  }
}

// ─── Nominatim fallback ───────────────────────────────────────────────────────
const NOM_BASE    = 'https://nominatim.openstreetmap.org/search';
const NOM_HEADERS = { 'User-Agent': 'SGOS-PGSaneamento/1.0' };

async function nominatim(params) {
  const qs = new URLSearchParams({
    format: 'json', limit: '1', 'accept-language': 'pt', countrycodes: 'br',
    ...params,
  }).toString();
  try {
    const res = await fetch(`${NOM_BASE}?${qs}`, { headers: NOM_HEADERS });
    if (!res.ok) return null;
    const data = await res.json();
    if (Array.isArray(data) && data.length > 0)
      return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    return null;
  } catch {
    return null;
  }
}

/**
 * Forward geocoding com cascata:
 *   1. Mapbox  — busca livre com número (mais preciso para endereços BR)
 *   2. Nominatim estruturado — CEP + street
 *   3. Nominatim estruturado — street + city + state
 *   4. Nominatim livre       — fallback final
 *
 * @param {{ logradouro?, numero?, bairro?, cidade?, uf?, cep? }} params
 * @returns {Promise<{lat: number, lng: number} | null>}
 */
export async function geocodeAddress({ logradouro, numero, bairro, cidade, uf, cep } = {}) {
  if (!logradouro) return null;

  const rua = numero ? `${logradouro} ${numero}` : logradouro;

  // 1) Mapbox (mais preciso — lida bem com numeração brasileira)
  const mb = await geocodeMapbox({ logradouro, numero, bairro, cidade, uf, cep });
  if (mb) return mb;

  // 2) Nominatim: CEP + rua com número
  if (cep) {
    const r = await nominatim({ postalcode: cep.replace(/\D/g, ''), street: rua });
    if (r) return r;
  }

  // 3) Nominatim: parâmetros estruturados
  if (cidade) {
    const params = { street: rua, city: cidade };
    if (uf) params.state = uf;
    const r = await nominatim(params);
    if (r) return r;
  }

  // 4) Nominatim: query livre
  const partes = [rua, bairro, cidade, uf].filter(Boolean);
  if (partes.length >= 2) {
    const r = await nominatim({ q: partes.join(', ') });
    if (r) return r;
  }

  return null;
}

